import type { SessionCreatedContext } from '@link/share/hooks';
import { pushGtmData, pushGtmEvent } from '@link/share/vendors/gtm';
import { buildEcommerceItems, type GAItem } from '@link/skytree/utils';

// GA4 Enhanced Ecommerce event names
const GA_EVENTS = {
  VIEW_ITEM: 'view_item',
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase'
} as const;

type GAEventName = (typeof GA_EVENTS)[keyof typeof GA_EVENTS];

// Nested ecommerce object (GA4 Enhanced Ecommerce payload)
interface GAEcommercePayload {
  currency: string;
  value: number;
  items: GAItem[];
}

// Params for view_item / begin_checkout
export interface GAEcommerceEventParams {
  currency: string;
  value: number;
  ecommerce: GAEcommercePayload;
}

// Params for purchase — requires transaction_id
export interface GAPurchaseEventParams extends GAEcommerceEventParams {
  transaction_id: string;
  /**
   * Fields the third-party CV vendor (dep.tc / lpcv.js) needs, read out of the
   * dataLayer by a GTM Custom HTML tag. The GA4 tag maps neither, so they stay
   * out of GA4. `cv_amount` is the settlement amount, not GA4's display `value`.
   */
  cv_amount: number;
  cv_info: string;
}

export function setUserId(userId: string) {
  pushGtmData({ user_id: userId });
}

function pushDataLayer(event: GAEventName, params: object) {
  pushGtmEvent({ event, ...params });
}

function trackViewItem(params: GAEcommerceEventParams) {
  pushDataLayer(GA_EVENTS.VIEW_ITEM, params);
}

export function trackBeginCheckout(params: GAEcommerceEventParams) {
  pushDataLayer(GA_EVENTS.BEGIN_CHECKOUT, params);
}

export function trackPurchase(params: GAPurchaseEventParams) {
  pushDataLayer(GA_EVENTS.PURCHASE, params);
}

/** Legacy view_item, fired on a successful session PUT. */
export function trackLegacyViewItem({
  activityId,
  activityTitle,
  planId,
  planTitle,
  agentId,
  currency,
  totalAmount,
  units
}: SessionCreatedContext) {
  trackViewItem({
    currency,
    value: totalAmount,
    ecommerce: {
      currency,
      value: totalAmount,
      items: buildEcommerceItems({
        activityId,
        activityTitle,
        planId,
        planTitle,
        agentId,
        units
      })
    }
  });
}
